// Per-challenge leaderboard — you vs the friends who joined this code, ranked
// by GM Score, identified by player ID (everyone shares one team, so the ID is
// the identity). Dark design surface. Phase 1 runs on the local stub backend,
// so the board usually shows just your entry with a waiting banner.

import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { challengeGradeColor } from "../util/challengeUi.ts";
import SummerLeagueMark from "../components/SummerLeagueMark.tsx";
import type { View } from "../../common/types.ts";

const ChallengeLeaderboard = ({
	code,
	rows,
	status,
}: View<"challengeLeaderboard">) => {
	useTitleBar({ title: "Challenge Leaderboard" });

	// Phase 1 has no group-size fact (backend stub) — "waiting" just means the
	// board is thin. Phase 2 wires finishedCount / groupSize from the service.
	const waiting = rows.length <= 1;

	return (
		<div className="chl">
			<style>{styles}</style>
			<a
				className="chl-back"
				href={helpers.leagueUrl(
					status === "complete" ? ["challenge_result"] : [],
				)}
			>
				‹ Back
			</a>
			<div className="chl-head">
				<SummerLeagueMark size={30} idSuffix="lb" />
				<div>
					<div className="chl-title">SUMMER 2026 · YOUR CHALLENGE</div>
					<div className="chl-sub">Code {code} · ranked by GM Score</div>
				</div>
			</div>

			{waiting ? (
				<div className="chl-waiting">
					<span className="chl-spinner" />
					Waiting on your group — results appear as friends finish.
				</div>
			) : null}

			{rows.length === 0 ? (
				<div className="chl-empty">
					No results yet. Finish your season to put a score on the board.
				</div>
			) : (
				<>
					<div className="chl-cols">
						<div>#</div>
						<div>PLAYER</div>
						<div className="c">GRADE</div>
						<div className="c">SCORE</div>
						<div className="r">W–L</div>
					</div>
					<div className="chl-rows">
						{rows.map((r) => (
							<div key={r.playerId} className={`chl-row ${r.isMe ? "me" : ""}`}>
								<div className="chl-rank">{r.rank}</div>
								<div className="chl-player">
									{r.playerId}
									{r.isMe ? " · You" : ""}
								</div>
								<div
									className="chl-grade"
									style={{ color: challengeGradeColor(r.grade) }}
								>
									{r.grade}
								</div>
								<div className="chl-score">{r.gmScore}</div>
								<div className="chl-record">
									{r.wins}–{r.losses}
								</div>
							</div>
						))}
					</div>
				</>
			)}
		</div>
	);
};

const styles = `
.chl { max-width:560px; margin:0 auto; padding:12px 16px 40px; color:#fff;
  font-family:Montserrat,system-ui,sans-serif; }
.chl-back { display:inline-block; font-size:13px; font-weight:800; color:#9fb0bc;
  text-decoration:none; padding:4px 0 12px; }
.chl-back:hover { color:#cfe0ec; }
.chl-head { display:flex; align-items:center; gap:10px; padding:0 2px; }
.chl-title { font-size:14px; font-weight:900; letter-spacing:.3px; }
.chl-sub { font-size:11px; color:#8a97a0; font-weight:600; margin-top:1px; }

.chl-waiting { margin-top:14px; background:rgba(24,224,168,.08);
  border:1px solid rgba(24,224,168,.28); border-radius:12px; padding:11px 13px; display:flex;
  align-items:center; gap:10px; font-size:12px; font-weight:700; color:#cfe0ec; }
.chl-spinner { width:12px; height:12px; border-radius:50%; flex:none;
  border:2px solid rgba(24,224,168,.4); border-top-color:#18E0A8; display:inline-block;
  animation:chlSpin .9s linear infinite; }
@keyframes chlSpin { to { transform:rotate(360deg); } }
.chl-empty { margin-top:14px; background:#181818; border:1px solid #232323; border-radius:12px;
  padding:20px; text-align:center; font-size:12.5px; font-weight:600; color:#9aa4ac; }

.chl-cols { display:grid; grid-template-columns:24px 1fr 42px 46px 58px; gap:6px;
  padding:14px 12px 8px; font-size:9px; font-weight:800; letter-spacing:.5px; color:#7f8b93; }
.chl-cols .c { text-align:center; }
.chl-cols .r { text-align:right; }
.chl-rows { display:flex; flex-direction:column; gap:5px; }
.chl-row { display:grid; grid-template-columns:24px 1fr 42px 46px 58px; gap:6px;
  align-items:center; padding:11px 12px; border-radius:11px; background:#181818;
  border:1px solid #232323; }
.chl-row.me { background:rgba(24,224,168,.08); border:1px solid rgba(24,224,168,.28);
  border-left:3px solid #18E0A8; }
.chl-rank { font-size:12px; font-weight:900; color:#7f8b93; }
.chl-row.me .chl-rank { color:#18E0A8; }
.chl-player { font-size:13.5px; font-weight:800; color:#e6ebee; letter-spacing:.3px; }
.chl-row.me .chl-player { color:#18E0A8; font-weight:900; }
.chl-grade { font-size:13px; font-weight:900; text-align:center; }
.chl-score { font-size:13px; font-weight:800; text-align:center; color:#b0b8bd; }
.chl-record { font-size:11.5px; font-weight:700; color:#b0b8bd; text-align:right; }
`;

export default ChallengeLeaderboard;
